import React from 'react';
import { TextField, IconButton, InputAdornment, SxProps, Theme, useTheme } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAppTheme } from '@contexts/ThemeContext';

interface CustomTextFieldProps {
    type?: 'text' | 'email' | 'password';
    placeholder?: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    required?: boolean;
    name?: string;
    id?: string;
    showPasswordToggle?: boolean;
    onTogglePassword?: () => void;
    showPassword?: boolean;
    fullWidth?: boolean;
    autoFocus?: boolean;
    sx?: SxProps<Theme>;
}

export const CustomTextField: React.FC<CustomTextFieldProps> = ({
    type = 'text',
    placeholder,
    value,
    onChange,
    onKeyDown,
    disabled = false,
    required = false,
    name,
    id,
    showPasswordToggle = false,
    onTogglePassword,
    showPassword = false,
    fullWidth = true,
    autoFocus = false,
    sx = {},
}) => {
    const { isDarkMode } = useAppTheme();
    const theme = useTheme();
    const inputType = type === 'password' && showPassword ? 'text' : type;

    return (
        <TextField
            fullWidth={fullWidth}
            placeholder={placeholder}
            type={inputType}
            name={name}
            id={id}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            margin="normal"
            sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                    borderRadius: '8px',
                    '& fieldset': {
                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : theme.palette.divider,
                        borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : theme.palette.text.primary,
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                        borderWidth: '2px',
                    },
                    '&.Mui-focused': {
                        '& fieldset': {
                            borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                            borderWidth: '2px',
                        }
                    },
                    '&.Mui-focused.MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                            borderWidth: '2px',
                        }
                    },
                    '&.Mui-focused.MuiOutlinedInput-root fieldset': {
                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                        borderWidth: '2px',
                    },
                },
                '& .MuiInputBase-input': {
                    color: theme.palette.text.primary,
                    backgroundColor: 'transparent',
                    padding: { xs: '16px 20px', sm: '18px 22px', md: '20px 24px' },
                    fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                    '&:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.action.hover} inset`,
                        WebkitTextFillColor: theme.palette.text.primary,
                        transition: 'background-color 8080s ease-in-out 0s',
                    },
                    '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.action.hover} inset`,
                        WebkitTextFillColor: theme.palette.text.primary,
                    },
                    '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.action.hover} inset`,
                        WebkitTextFillColor: theme.palette.text.primary,
                    },
                },
                '& .MuiInputBase-input::placeholder': {
                    color: theme.palette.text.secondary,
                    opacity: 0.7,
                },
                ...sx,
            }}
            InputProps={{
                ...(showPasswordToggle && onTogglePassword && {
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={onTogglePassword}
                                disabled={disabled}
                                edge="end"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    '&:hover': {
                                        color: theme.palette.text.primary,
                                    }
                                }}
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }),
            }}
        />
    );
};
